import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ParseParams } from 'zod';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import type { RoutingDto } from '../../api/models/RoutingDto';
// Updated RoutingDto with minDoughQuantity and maxDoughQuantity
import type { OperationDto } from '../../api/models/OperationDto';
import type { OperationRelationshipDto } from '../../api/models/OperationRelationshipDto';
import type { ResourceRequirementDto } from '../../api/models/ResourceRequirementDto';
import type { MaterialDto } from '../../api/models/MaterialDto';
import type { CrudFormProps } from '../../components/CrudPage';
import { ReactTabs } from '../../components/ReactTabs';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';
import { CrudPage } from '../../components/CrudPage';
import { MaterialControllerService } from '../../api/services/MaterialControllerService';
import InputQuantitiesDialog from '../../components/InputQuantitiesDialog';
import ResourceSelectionDialog from '../../components/ResourceSelectionDialog';
import type { OperationInputQuantityDto } from '../../api/models/OperationInputQuantityDto';
import { useDropdownFormInitializer } from '../../hooks/useDropdownFormInitializer';
import OperationDetailDialog from './OperationDetailDialog';
import OperationRelationshipDetailDialog from './OperationRelationshipDetailDialog';
import ResourceRequirementDetailDialog from './ResourceRequirementDetailDialog';
import { generateTempUUID } from '../../utils/uuid';
import { getRoutingWeekdayVariantsFlag } from '../../utils/featureFlags';
import {
  createRoutingErrorMap,
  createRoutingSchema,
  summarizeRoutingFormErrors,
  toOperationRelationshipDtos,
  toResourceRequirementDtos,
  type RoutingFormData,
} from './routingFormSchema';
import { linesToHtml } from '../../utils/escapeHtml';

type RoutingApplicabilityValue = 'DEFAULT' | 'WEEKDAYS' | 'HOLIDAY' | 'INACTIVE';

const APPLICABILITY_VALUES: RoutingApplicabilityValue[] = ['DEFAULT', 'WEEKDAYS', 'HOLIDAY', 'INACTIVE'];

const normalizeApplicability = (raw: unknown): RoutingApplicabilityValue => {
  if (typeof raw === 'string' && (APPLICABILITY_VALUES as string[]).includes(raw)) {
    return raw as RoutingApplicabilityValue;
  }
  return 'DEFAULT';
};

interface RoutingFormDialogProps extends CrudFormProps<RoutingDto & { id: number }> {
  // Force TypeScript to recompile
}

// The three nested CrudPages (operations, relationships, resource requirements) get their form
// components from module scope. They used to be inline arrow functions, which gave React a new
// component type on every render of RoutingFormDialog. Any re-render of the routing dialog (a
// token refresh replaces the user profile in the store roughly every two minutes, which
// re-renders the whole page tree) then unmounted the open detail dialog and mounted a fresh,
// empty one: the user lost every typed value and the form looked like it "reset itself".
// Data from the routing form reaches the adapters through CrudPage's formProps instead.

type OperationFormAdapterProps = CrudFormProps<any> & { routingOutputMaterialId?: string };

export const OperationFormAdapter: React.FC<OperationFormAdapterProps> = ({ value, onClose, onSave, routingOutputMaterialId }) => {
  // Map id back to uuid for OperationDetailDialog
  // Ensure all fields are preserved
  // Memoized on value identity: the detail dialog re-initializes its form state whenever the
  // value object changes, so a freshly mapped object on every render would wipe unsaved edits.
  const operationWithUuid = useMemo(() => value ? {
    ...value,
    uuid: value.id || value.uuid,
    // Explicitly preserve all operation fields
    name: value.name,
    description: value.description,
    primaryResourceTypeId: value.primaryResourceTypeId,
    timeType: value.timeType,
    durationSeconds: value.durationSeconds,
    durationPerPieceSeconds: value.durationPerPieceSeconds,
    inputQuantities: value.inputQuantities || [],
    operationModes: value.operationModes || [],
    setupAttributeValues: value.setupAttributeValues || [],
    batchingAttributeValues: value.batchingAttributeValues || [],
    parallelProcessing: value.parallelProcessing,
    capacityDemandType: value.capacityDemandType,
    capacityDemand: value.capacityDemand,
    minCapacityDemand: value.minCapacityDemand,
    maxCapacityDemand: value.maxCapacityDemand
  } : value, [value]);
  return (
    <OperationDetailDialog
      value={operationWithUuid}
      onClose={onClose}
      onSave={(operationData: any) => {
        // Map uuid back to id for CrudPage
        const operationWithId = { ...operationData, id: operationData.uuid };
        onSave(operationWithId);
      }}
      routingOutputMaterialId={routingOutputMaterialId}
    />
  );
};

type OperationRelationshipFormAdapterProps = CrudFormProps<any> & {
  availableOperations?: OperationDto[];
  allRelationships?: OperationRelationshipDto[];
  routingMaterialId?: string;
};

export const OperationRelationshipFormAdapter: React.FC<OperationRelationshipFormAdapterProps> = ({
  value, onClose, onSave, availableOperations, allRelationships, routingMaterialId,
}) => {
  // Map id back to uuid for OperationRelationshipDetailDialog
  // Ensure all fields are preserved, especially minDistance and maxDistance
  // IMPORTANT: 0 is a valid value for maxDistance, so we need to check explicitly for undefined/null
  // Memoized on value identity (see OperationFormAdapter).
  const relationshipWithUuid = useMemo(() => value ? {
    ...value,
    uuid: value.id || value.uuid,
    // minDistance: 0 is valid, so only check for undefined/null
    minDistance: value.minDistance !== undefined && value.minDistance !== null
      ? (typeof value.minDistance === 'string' ? Number(value.minDistance) : value.minDistance)
      : 0,
    // maxDistance: 0 is valid, so only check for undefined/null (not falsy)
    maxDistance: value.maxDistance !== undefined && value.maxDistance !== null
      ? (typeof value.maxDistance === 'string' ? Number(value.maxDistance) : value.maxDistance)
      : undefined,
    relationType: value.relationType || null,
    relationshipCategory: value.relationshipCategory || 'TIME',
    resourceConstraints: value.resourceConstraints || null
  } : value, [value]);
  return (
    <OperationRelationshipDetailDialog
      value={relationshipWithUuid}
      onClose={onClose}
      onSave={(relationshipData: any) => {
        // Map uuid back to id for CrudPage
        const relationshipWithId = { ...relationshipData, id: relationshipData.uuid };
        onSave(relationshipWithId);
      }}
      availableOperations={availableOperations || []}
      allRelationships={allRelationships || []}
      exemplarQuantity={value?.minDoughQuantity ? Number(value.minDoughQuantity) : undefined}
      routingMaterialId={routingMaterialId}
    />
  );
};

type ResourceRequirementFormAdapterProps = CrudFormProps<any> & { availableOperations?: OperationDto[] };

export const ResourceRequirementFormAdapter: React.FC<ResourceRequirementFormAdapterProps> = ({
  value, onClose, onSave, availableOperations,
}) => {
  // Map id back to uuid for ResourceRequirementDetailDialog
  // Ensure all fields are preserved, especially fromOperationId, toOperationId, fromRelation, toRelation, and resources
  // Memoized on value identity (see OperationFormAdapter).
  const requirementWithUuid = useMemo(() => value ? {
    ...value,
    uuid: value.uuid || value.id,
    // Explicitly preserve all resource requirement fields
    fromOperationId: value.fromOperationId,
    toOperationId: value.toOperationId,
    fromRelation: value.fromRelation,
    toRelation: value.toRelation,
    resources: value.resources || [],
    conditional: value.conditional,
    conditionalOperationId: value.conditionalOperationId
  } : value, [value]);
  return (
    <ResourceRequirementDetailDialog
      value={requirementWithUuid}
      onClose={onClose}
      onSave={(requirementData: any) => {
        // Map uuid back to id for CrudPage
        // Ensure uuid is preserved
        const requirementWithId = {
          ...requirementData,
          id: requirementData.uuid || requirementData.id,
          uuid: requirementData.uuid || requirementData.id
        };
        onSave(requirementWithId);
      }}
      availableOperations={availableOperations || []}
    />
  );
};

const RoutingFormDialog: React.FC<RoutingFormDialogProps> = ({
  value,
  onClose,
  onSave,
  isClone,
  routingType,
  materialType,
}) => {
  const { t } = useTranslation(['common']);
  const resolvedRoutingType: 'PRODUCT_ROUTING' | 'INTERMEDIATE_ROUTING' =
    routingType || (value?.routingType === 'INTERMEDIATE_ROUTING' ? 'INTERMEDIATE_ROUTING' : 'PRODUCT_ROUTING');
  const resolvedMaterialType: 'PRODUCT' | 'INTERMEDIATE_PRODUCT' =
    materialType === 'INTERMEDIATE_PRODUCT'
      ? 'INTERMEDIATE_PRODUCT'
      : (resolvedRoutingType === 'INTERMEDIATE_ROUTING' ? 'INTERMEDIATE_PRODUCT' : 'PRODUCT');
  const materialLabel =
    resolvedRoutingType === 'INTERMEDIATE_ROUTING'
      ? t('routing.form.materialLabel.intermediate', 'Vorprodukt')
      : t('routing.form.materialLabel.product', 'Produkt');
  const [visible, setVisible] = useState(false);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<OperationDto | null>(null);
  const [inputQuantitiesDialogOpen, setInputQuantitiesDialogOpen] = useState(false);
  const [selectedResourceRequirement, setSelectedResourceRequirement] = useState<ResourceRequirementDto | null>(null);
  const [resourceSelectionDialogOpen, setResourceSelectionDialogOpen] = useState(false);
  const [_selectedMaterial, setSelectedMaterial] = useState<MaterialDto | null>(null);
  const [deleteOperationsDialogOpen, setDeleteOperationsDialogOpen] = useState(false);
  const [pendingDeleteOperations, setPendingDeleteOperations] = useState<any[]>([]);
  const [deleteOperationsMessage, setDeleteOperationsMessage] = useState<string>('');
  const [deleteOperationsSimpleDialogOpen, setDeleteOperationsSimpleDialogOpen] = useState(false);
  const [pendingDeleteOperationsSimple, setPendingDeleteOperationsSimple] = useState<any[]>([]);
  const [moveInfoDialogOpen, setMoveInfoDialogOpen] = useState(false);
  const [movedOperation, setMovedOperation] = useState<OperationDto | null>(null);
  const [movedOperationRelationships, setMovedOperationRelationships] = useState<{
    asPredecessor: OperationRelationshipDto[];
    asSuccessor: OperationRelationshipDto[];
  } | null>(null);
  const [relationshipSuggestionDialogOpen, setRelationshipSuggestionDialogOpen] = useState(false);
  const [relationshipSuggestionData, setRelationshipSuggestionData] = useState<{
    predecessor: OperationDto;
    successor: OperationDto;
    newOperation: OperationDto;
    existingRelationship: OperationRelationshipDto;
  } | null>(null);
  const [relationshipSuggestionOption, setRelationshipSuggestionOption] = useState<'resolve' | 'keep' | 'delete'>('resolve');
  const [_relationOptions] = useState([
    { text: t('form.relationType.START'), value: 'START' },
    { text: t('form.relationType.END'), value: 'END' }
  ]);
  

  const routingSchema = React.useMemo(() => createRoutingSchema(t), [t]);
  const routingErrorMap = React.useMemo(() => createRoutingErrorMap(t), [t]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    control,
    formState: { errors, isSubmitting }
  } = useForm<RoutingFormData>({
    // zod reads the parse params partially at runtime (path and async have defaults); the
    // resolver's type asks for the full ParseParams, hence the cast.
    resolver: zodResolver(routingSchema, { errorMap: routingErrorMap } as ParseParams),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: {
      name: '',
      description: '',
      materialId: '',
      routingType: resolvedRoutingType,
      weekdayMask: 0,
      applicability: 'DEFAULT',
      operations: [],
      operationRelationships: [],
      resourceRequirements: []
    }
  });

  const currentValue = watch(); // This causes re-renders on every change - could be optimized with specific field watching
  const [operationsRefreshKey, setOperationsRefreshKey] = useState(0);
  const [operations, setOperations] = useState<any[]>([]);
  const [operationRelationshipsRefreshKey, setOperationRelationshipsRefreshKey] = useState(0);
  const [operationRelationships, setOperationRelationships] = useState<any[]>([]);
  const [resourceRequirementsRefreshKey, setResourceRequirementsRefreshKey] = useState(0);
  const [resourceRequirements, setResourceRequirements] = useState<any[]>([]);
  /** Only after the material dropdown + reset(): otherwise CrudPage mounts too early and load() sees empty lists */
  const [nestedGridsReady, setNestedGridsReady] = useState(false);

  // Weekday variants: feature flag gates the inline checkbox section above the tabs.
  // The mask is now a regular form field (weekdayMask) and is saved together with the routing —
  // the backend's saveRoutingWithSubEntities path picks it up and validates overlap with siblings.
  const [variantsFeatureEnabled, setVariantsFeatureEnabled] = useState<boolean>(false);

  useEffect(() => {
    getRoutingWeekdayVariantsFlag()
      .then((res) => setVariantsFeatureEnabled(Boolean(res?.enabled)))
      .catch(() => setVariantsFeatureEnabled(false));
  }, []);

  // Since V00138 'Standard' is an explicit applicability=DEFAULT value, no longer an implicit
  // mask=0 special case. The save gate from the old model (needsWeekdayPick) is gone;
  // the backend rejects conflicts (second DEFAULT / second HOLIDAY) with a clear message.

  // Handler for the toolbar 'Add' action - always inserts at the end
  const handleOperationsAdd = React.useCallback((_selectedItems: any[]) => {
    const currentOps = currentValue.operations || [];
    
    // Always insert at the end: newOperationNumber = max(operationNumber) + 1
    const maxOperationNumber = currentOps.length > 0 
      ? Math.max(...currentOps.map(op => (op as any).operationNumber || 0))
      : 0;
    const newOperationNumber = maxOperationNumber + 1;
    
    // Create the new operation but do NOT insert it into the list yet;
    // it is inserted only when the dialog is saved
    const newOperation = {
      uuid: generateTempUUID(),
      operationNumber: newOperationNumber,
      name: '',
      description: '',
      durationSeconds: undefined,
      inputQuantities: []
    } as OperationDto;
    
    // Return the new operation so CrudPage can open the dialog with it
    const operationWithId = { ...newOperation, id: newOperation.uuid };
    return operationWithId;
  }, [currentValue.operations]);

  // State for the context menu: operation to insert relative to
  const [pendingInsertOperation, setPendingInsertOperation] = React.useState<any | null>(null);
  const [operationDetailDialogOpen, setOperationDetailDialogOpen] = React.useState(false);

  // Handler for the context menu action 'Add before'
  const handleAddOperationBefore = React.useCallback((targetOperation: any) => {
    const currentOps = currentValue.operations || [];
    const targetOp = currentOps.find(op => op.uuid === targetOperation.uuid || op.uuid === targetOperation.id);
    
    if (!targetOp) return;
    
    // newOperationNumber = targetOperation.operationNumber (takes over its position)
    const targetOperationNumber = (targetOp as any).operationNumber || 1;
    
    const newOperation = {
      uuid: generateTempUUID(),
      operationNumber: targetOperationNumber,
      name: '',
      description: '',
      durationSeconds: undefined,
      inputQuantities: []
    } as OperationDto;
    
    const operationWithId = { ...newOperation, id: newOperation.uuid };
    setPendingInsertOperation(operationWithId);
    setOperationDetailDialogOpen(true);
  }, [currentValue.operations]);

  // Handler for the context menu action 'Add after'
  const handleAddOperationAfter = React.useCallback((targetOperation: any) => {
    const currentOps = currentValue.operations || [];
    const targetOp = currentOps.find(op => op.uuid === targetOperation.uuid || op.uuid === targetOperation.id);
    
    if (!targetOp) return;
    
    // newOperationNumber = targetOperation.operationNumber + 1
    const targetOperationNumber = (targetOp as any).operationNumber || 1;
    
    const newOperation = {
      uuid: generateTempUUID(),
      operationNumber: targetOperationNumber + 1,
      name: '',
      description: '',
      durationSeconds: undefined,
      inputQuantities: []
    } as OperationDto;
    
    const operationWithId = { ...newOperation, id: newOperation.uuid };
    setPendingInsertOperation(operationWithId);
    setOperationDetailDialogOpen(true);
  }, [currentValue.operations]);

  // Handler for context menu clicks
  const handleOperationsContextMenuClick = React.useCallback((args: { item: { id: string; text: string }; rowInfo?: { rowData: any; rowIndex: number } }) => {
    if (!args.rowInfo?.rowData) return;
    
    const targetOperation = args.rowInfo.rowData;
    
    switch (args.item.id) {
      case 'addBefore':
        handleAddOperationBefore(targetOperation);
        break;
      case 'addAfter':
        handleAddOperationAfter(targetOperation);
        break;
    }
  }, [handleAddOperationBefore, handleAddOperationAfter]);

  // Handler for saving from the context menu dialog
  const handleInsertOperationSave = React.useCallback((operationData: any) => {
    // Remove the id field and use uuid for the actual data
    const { id, ...opData } = operationData;
    
    // Replicate the saveData logic from CrudPage
    const targetOperationNumber = (opData as any).operationNumber;
    const currentOps = currentValue.operations || [];
    
    // Find operations that need to be shifted (operationNumber >= targetOperationNumber)
    const operationsToShift = currentOps.filter(op => {
      const opNumber = (op as any).operationNumber || 0;
      return opNumber >= targetOperationNumber;
    });
    
    // Shift operationNumbers: increment by 1
    const shiftedOperations = operationsToShift.map(op => ({
      ...op,
      operationNumber: ((op as any).operationNumber || 0) + 1
    }));
    
    // Operations that don't need shifting
    const unchangedOperations = currentOps.filter(op => {
      const opNumber = (op as any).operationNumber || 0;
      return opNumber < targetOperationNumber;
    });
    
    // Combine: unchanged operations + new operation + shifted operations
    const updatedOperations = [
      ...unchangedOperations,
      { ...opData, uuid: opData.uuid || generateTempUUID() },
      ...shiftedOperations
    ];
    
    // Sort by operationNumber to ensure correct order
    updatedOperations.sort((a, b) => {
      const aNum = (a as any).operationNumber || 0;
      const bNum = (b as any).operationNumber || 0;
      return aNum - bNum;
    });
    
    setValue('operations', updatedOperations);
    setOperations(updatedOperations);
    setOperationsRefreshKey(prev => prev + 1);
    
    // Close the dialog and reset the state
    setOperationDetailDialogOpen(false);
    setPendingInsertOperation(null);
  }, [currentValue.operations, setValue]);

  // Step 1b: analysis on delete - finds affected relationships and resource requirements
  const analyzeOperationDeletion = React.useCallback((operationToDelete: OperationDto) => {
    const currentRels = currentValue.operationRelationships || [];
    const currentRRs = currentValue.resourceRequirements || [];
    
    // Find affected relationships
    const affectedRelationships = currentRels.filter(rel =>
      rel.predecessorId === operationToDelete.uuid ||
      rel.successorId === operationToDelete.uuid
    );
    
    // Find affected resource requirements
    const affectedResourceRequirements = currentRRs.filter(rr =>
      rr.fromOperationId === operationToDelete.uuid ||
      rr.toOperationId === operationToDelete.uuid ||
      (rr.conditional && rr.conditionalOperationId === operationToDelete.uuid)
    );
    
    return {
      operation: operationToDelete,
      affectedRelationships,
      affectedResourceRequirements
    };
  }, [currentValue.operationRelationships, currentValue.resourceRequirements]);

  // Handler for deletion with confirmation and dependency check
  const handleOperationsDelete = React.useCallback((selectedItems: any[]) => {
    if (selectedItems.length === 0) return;
    
    const operationsToDelete = selectedItems.map(item => {
      const op = (currentValue.operations || []).find(o => o.uuid === item.uuid || o.uuid === item.id);
      return op;
    }).filter((op): op is NonNullable<typeof op> => op !== undefined);
    
    if (operationsToDelete.length === 0) return;
    
    // Analyse the dependencies of every operation that is to be deleted
    const analyses = operationsToDelete.map(op => analyzeOperationDeletion({
      ...op,
      durationSeconds: op.durationSeconds ?? undefined
    } as OperationDto));
    
    // Check whether there are any dependencies
    const hasDependencies = analyses.some(analysis => 
      analysis.affectedRelationships.length > 0 || 
      analysis.affectedResourceRequirements.length > 0
    );
    
    if (!hasDependencies) {
      // No dependencies: simple confirmation dialog
      setPendingDeleteOperationsSimple(operationsToDelete);
      setDeleteOperationsSimpleDialogOpen(true);
      return;
    }
    
    // There are dependencies: extended dialog
    const allAffectedRelationships = analyses.flatMap(a => a.affectedRelationships);
    const allAffectedResourceRequirements = analyses.flatMap(a => a.affectedResourceRequirements);
    
    const operationNames = operationsToDelete.map(op => op.name || 'Unbenannt').join(', ');
    
    // Build the message from translation keys
    const parts: string[] = [];
    parts.push(t('messages.deleteOperationsWithDependencies.title', { operationNames, defaultValue: `Möchten Sie die Vorgänge "${operationNames}" wirklich löschen?` }));
    
    if (allAffectedRelationships.length > 0) {
      parts.push(t('messages.deleteOperationsWithDependencies.affectedRelationships', { count: allAffectedRelationships.length, defaultValue: `Betroffene Vorgangsbeziehungen (${allAffectedRelationships.length}):` }));
      allAffectedRelationships.forEach(rel => {
        const predOp = (currentValue.operations || []).find(op => op.uuid === rel.predecessorId);
        const succOp = (currentValue.operations || []).find(op => op.uuid === rel.successorId);
        const predName = predOp?.name || 'Unbekannt';
        const succName = succOp?.name || 'Unbekannt';
        parts.push(`  • ${t('messages.deleteOperationsWithDependencies.relationshipItem', { predName, succName, defaultValue: `${predName} → ${succName}` })}`);
      });
      parts.push('');
    }
    
    if (allAffectedResourceRequirements.length > 0) {
      parts.push(t('messages.deleteOperationsWithDependencies.affectedResourceRequirements', { count: allAffectedResourceRequirements.length, defaultValue: `Betroffene Ressourcenanforderungen (${allAffectedResourceRequirements.length}):` }));
      allAffectedResourceRequirements.forEach(rr => {
        const fromOp = (currentValue.operations || []).find(op => op.uuid === rr.fromOperationId);
        const toOp = (currentValue.operations || []).find(op => op.uuid === rr.toOperationId);
        const fromName = fromOp?.name || 'Unbekannt';
        const toName = toOp?.name || 'Unbekannt';
        parts.push(`  • ${t('messages.deleteOperationsWithDependencies.resourceRequirementItem', { fromName, toName, defaultValue: `${fromName} → ${toName}` })}`);
      });
      parts.push('');
    }
    
    parts.push(t('messages.deleteOperationsWithDependencies.willBeDeleted', { defaultValue: 'Diese Beziehungen und Anforderungen werden mitgelöscht.' }));
    
    // Convert to HTML for display
    const htmlMessage = parts.join('<br />');
    
    // Store the operations to delete and the message
    setPendingDeleteOperations(operationsToDelete);
    setDeleteOperationsMessage(htmlMessage);
    setDeleteOperationsDialogOpen(true);
  }, [currentValue.operations, t, analyzeOperationDeletion]);

  // Confirmation callback for deleting operations without dependencies
  const confirmDeleteOperationsSimple = React.useCallback(() => {
    if (pendingDeleteOperationsSimple.length === 0) return;
    
    const uuidsToDelete = pendingDeleteOperationsSimple.map(op => op.uuid);
    // Sort by operationNumber first - the order in currentValue.operations is not guaranteed
    const sorted = [...(currentValue.operations || [])].sort((a, b) => {
      const aNum = (a as any).operationNumber ?? 999;
      const bNum = (b as any).operationNumber ?? 999;
      return aNum - bNum;
    });
    const filteredOps = sorted.filter(op => !uuidsToDelete.includes(op.uuid));
    // Update operationNumber for all remaining operations (1-based)
    const updatedOps = filteredOps.map((op, index) => ({
      ...op,
      operationNumber: index + 1
    }));
    setValue('operations', updatedOps);
    setOperations(updatedOps);
    setOperationsRefreshKey(prev => prev + 1);
    
    // Close the dialog and clean up
    setDeleteOperationsSimpleDialogOpen(false);
    setPendingDeleteOperationsSimple([]);
  }, [pendingDeleteOperationsSimple, currentValue.operations, setValue]);

  // Confirmation callback for deleting operations
  const confirmDeleteOperations = React.useCallback(() => {
    if (pendingDeleteOperations.length === 0) return;
    
    const uuidsToDelete = pendingDeleteOperations.map(op => op.uuid);
    
    // Sort by operationNumber first - the order in currentValue.operations is not guaranteed
    const sorted = [...(currentValue.operations || [])].sort((a, b) => {
      const aNum = (a as any).operationNumber ?? 999;
      const bNum = (b as any).operationNumber ?? 999;
      return aNum - bNum;
    });
    // Delete operations
    const filteredOps = sorted.filter(op => !uuidsToDelete.includes(op.uuid));
    const updatedOps = filteredOps.map((op, index) => ({
      ...op,
      operationNumber: index + 1
    }));
    
    // Delete affected relationships
    const updatedRels = (currentValue.operationRelationships || []).filter(rel =>
      !uuidsToDelete.includes(rel.predecessorId || '') &&
      !uuidsToDelete.includes(rel.successorId || '')
    );
    
    // Delete affected resource requirements
    const updatedRRs = (currentValue.resourceRequirements || []).filter(rr =>
      !uuidsToDelete.includes(rr.fromOperationId || '') &&
      !uuidsToDelete.includes(rr.toOperationId || '') &&
      !(rr.conditional && uuidsToDelete.includes(rr.conditionalOperationId || ''))
    );
    
    setValue('operations', updatedOps);
    setValue('operationRelationships', updatedRels);
    setValue('resourceRequirements', updatedRRs);
    setOperations(updatedOps);
    setOperationsRefreshKey(prev => prev + 1);
    
    // Close the dialog and clean up
    setDeleteOperationsDialogOpen(false);
    setPendingDeleteOperations([]);
    setDeleteOperationsMessage('');
  }, [pendingDeleteOperations, currentValue.operations, currentValue.operationRelationships, currentValue.resourceRequirements, setValue]);

  const constraintTypeRenderer = (data: any): React.ReactNode => {
    // Syncfusion Grid passes data in different ways - try all possibilities
    const relationship = data?.data || data?.rowData || data;
    const constraints = relationship?.resourceConstraints;
    if (!constraints || !constraints.constraintType) return <span>-</span>;
    
    const constraintType = constraints.constraintType;
    // Map backend types to frontend types for display
    let displayType: string;
    if (constraintType === 'SAME_RESOURCE' || constraintType === 'RESOURCE_SHARED') {
      displayType = 'sameResource';
    } else if (constraintType === 'DIFFERENT_RESOURCES' || constraintType === 'RESOURCE_EXCLUSIVE') {
      displayType = 'differentResource';
    } else if (constraintType === 'CONSTRAINED_RESOURCES' || constraintType === 'RESOURCE_DEPENDENT') {
      displayType = 'restrictAllowedResources';
    } else {
      displayType = constraintType.toLowerCase();
    }
    
    const translation = t(`routing.form.constraintTypes.${displayType}`, constraintType);
    return <span>{String(translation)}</span>;
  };

  const dialogContentRef = React.useRef<HTMLDivElement | null>(null);
  const formTopRef = React.useRef<HTMLDivElement | null>(null);
  const tabsContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [gridHeightPx, setGridHeightPx] = useState(320);

  useLayoutEffect(() => {
    const updateGridHeight = () => {
      const contentEl = dialogContentRef.current;
      const tabsEl = tabsContainerRef.current;
      if (!contentEl || !tabsEl) return;

      const contentHeight = contentEl.clientHeight;
      const topHeight = formTopRef.current?.getBoundingClientRect().height ?? 0;
      const tabHeaderEl = tabsEl.querySelector('.e-tab-header') as HTMLElement | null;
      const tabHeaderHeight = tabHeaderEl?.getBoundingClientRect().height ?? 0;

      const available = contentHeight - topHeight - tabHeaderHeight - 8;
      setGridHeightPx(Math.max(240, Math.floor(available)));
    };

    const scheduleUpdate = () => window.requestAnimationFrame(updateGridHeight);
    scheduleUpdate();

    window.addEventListener('resize', scheduleUpdate);
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    if (dialogContentRef.current) resizeObserver.observe(dialogContentRef.current);
    if (formTopRef.current) resizeObserver.observe(formTopRef.current);
    if (tabsContainerRef.current) resizeObserver.observe(tabsContainerRef.current);

    return () => {
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, []);

  // Initialize operations state when form data changes
  useEffect(() => {
    setOperations(currentValue.operations || []);
  }, [currentValue.operations]);

  // Initialize operation relationships state when form data changes
  useEffect(() => {
    setOperationRelationships(currentValue.operationRelationships || []);
  }, [currentValue.operationRelationships]);

  // After inserting an operation, check whether there is a relationship suggestion
  const prevOperationsRef = React.useRef<OperationDto[]>([]);
  useEffect(() => {
    const currentOps = currentValue.operations || [];
    const prevOps = prevOperationsRef.current;
    
    // Find the newly inserted operation (by comparing the lists)
    if (currentOps.length > prevOps.length) {
      const newOp = currentOps.find(op => !prevOps.some(prevOp => prevOp.uuid === op.uuid));
      
      if (newOp) {
        const currentRels = currentValue.operationRelationships || [];
        const newOpIndex = currentOps.findIndex(op => op.uuid === newOp.uuid);
        
        // Check: is there a relationship between predecessor and successor?
        const predecessor = currentOps[newOpIndex - 1];
        const successor = currentOps[newOpIndex + 1];
        
        if (predecessor && successor) {
          const existingRel = currentRels.find(rel => 
            rel.predecessorId === predecessor.uuid && 
            rel.successorId === successor.uuid &&
            rel.relationshipCategory === 'TIME' // Time dependencies only
          );
          
          if (existingRel && !relationshipSuggestionDialogOpen && existingRel.predecessorId && existingRel.successorId && existingRel.relationshipCategory) {
            // Show the dialog
            setRelationshipSuggestionData({
              predecessor: {
                ...predecessor,
                durationSeconds: predecessor.durationSeconds ?? undefined
              } as OperationDto,
              successor: {
                ...successor,
                durationSeconds: successor.durationSeconds ?? undefined
              } as OperationDto,
              newOperation: {
                ...newOp,
                durationSeconds: newOp.durationSeconds ?? undefined
              } as OperationDto,
              existingRelationship: {
                ...existingRel,
                predecessorId: existingRel.predecessorId,
                successorId: existingRel.successorId,
                relationshipCategory: existingRel.relationshipCategory,
                minDistance: existingRel.minDistance ?? undefined,
                maxDistance: existingRel.maxDistance ?? undefined,
                resourceConstraints: existingRel.resourceConstraints ? {
                  ...existingRel.resourceConstraints,
                  constraintType: existingRel.resourceConstraints.constraintType || undefined
                } : undefined
              } as OperationRelationshipDto
            });
            setRelationshipSuggestionOption('resolve');
            setRelationshipSuggestionDialogOpen(true);
          }
        }
      }
    }
    
    prevOperationsRef.current = currentOps.map(op => ({
      ...op,
      durationSeconds: op.durationSeconds ?? undefined
    })) as OperationDto[];
  }, [currentValue.operations, currentValue.operationRelationships, relationshipSuggestionDialogOpen]);

  // Initialize resource requirements state when form data changes
  useEffect(() => {
    setResourceRequirements(currentValue.resourceRequirements || []);
  }, [currentValue.resourceRequirements]);




  // Load data function for the hook
  const loadData = async () => {
    // Use MaterialControllerService instead of MaterialControllerApi
    const materialsData = await MaterialControllerService.findAll();
    // Filter materials to match routing type context
    const makeMaterials = materialsData.filter((material: any) => 
      material.productionType === 'MAKE' && material.materialType === resolvedMaterialType && !material.isAlias
    );
    return { materials: makeMaterials };
  };

  // Set state functions for the hook
  const setStateFunctions = {
    materials: setMaterials
  };

  // Get initial values function that always returns fresh values from value prop
  const getInitialValues = React.useCallback((): RoutingFormData => {
    if (value) {
      return {
        name: value.name || '',
        description: value.description || '',
        materialId: value.materialId || '',
        routingType: (value.routingType === 'PRODUCT_ROUTING' || value.routingType === 'INTERMEDIATE_ROUTING')
          ? value.routingType
          : resolvedRoutingType,
        weekdayMask: typeof (value as RoutingDto & { weekdayMask?: unknown }).weekdayMask === 'number'
          ? (value as RoutingDto & { weekdayMask?: number }).weekdayMask!
          : Number((value as RoutingDto & { weekdayMask?: unknown }).weekdayMask ?? 0),
        applicability: normalizeApplicability(value.applicability),
        operations: value.operations || [],
        operationRelationships: value.operationRelationships || [],
        resourceRequirements: value.resourceRequirements || []
      };
    }
    return {
      name: '',
      description: '',
      materialId: '',
      routingType: resolvedRoutingType,
      weekdayMask: 0,
      applicability: 'DEFAULT' as const,
      operations: [],
      operationRelationships: [],
      resourceRequirements: []
    };
  }, [value, resolvedRoutingType]);

  // Use the custom hook for proper timing
  useDropdownFormInitializer(
    visible,
    loadData,
    setStateFunctions,
    reset,
    getInitialValues,
    () => setNestedGridsReady(true)
  );

  // Set dialog visible when component mounts
  useEffect(() => {
    setVisible(true);
  }, [value]);

  useEffect(() => {
    if (!visible) {
      setNestedGridsReady(false);
    }
  }, [visible]);

  // Reset form when value changes (especially important for cloning)
  // This runs AFTER useDropdownFormInitializer to ensure materials are loaded first
  useEffect(() => {
    if (value && visible && materials.length > 0) {
      const currentMaterialId = watch('materialId');
      const newMaterialId = value.materialId || '';
      const currentOperations = watch('operations') || [];
      const currentOperationRelationships = watch('operationRelationships') || [];
      const currentResourceRequirements = watch('resourceRequirements') || [];
      
      
      // Reset if:
      // 1. materialId has changed or is missing
      // 2. operations have changed (important for cloning)
      // 3. operationRelationships have changed (important for cloning)
      // 4. resourceRequirements have changed (important for cloning)
      // 5. it's a clone operation (to ensure all data is properly initialized)
      const operationsChanged = (value.operations?.length || 0) !== currentOperations.length ||
        JSON.stringify(value.operations || []) !== JSON.stringify(currentOperations);
      const operationRelationshipsChanged = (value.operationRelationships?.length || 0) !== currentOperationRelationships.length ||
        JSON.stringify(value.operationRelationships || []) !== JSON.stringify(currentOperationRelationships);
      const resourceRequirementsChanged = (value.resourceRequirements?.length || 0) !== currentResourceRequirements.length ||
        JSON.stringify(value.resourceRequirements || []) !== JSON.stringify(currentResourceRequirements);
      
      if (currentMaterialId !== newMaterialId || !currentMaterialId || operationsChanged || operationRelationshipsChanged || resourceRequirementsChanged || isClone) {
        
        reset({
          name: value.name || '',
          description: value.description || '',
          materialId: newMaterialId,
          routingType: (value.routingType === 'PRODUCT_ROUTING' || value.routingType === 'INTERMEDIATE_ROUTING')
            ? value.routingType
            : resolvedRoutingType,
          weekdayMask: typeof (value as RoutingDto & { weekdayMask?: unknown }).weekdayMask === 'number'
            ? (value as RoutingDto & { weekdayMask?: number }).weekdayMask!
            : Number((value as RoutingDto & { weekdayMask?: unknown }).weekdayMask ?? 0),
          applicability: normalizeApplicability(value.applicability),
          operations: value.operations || [],
          operationRelationships: value.operationRelationships || [],
          resourceRequirements: value.resourceRequirements || []
        });
      }
    }
  }, [value, visible, materials.length, reset, watch, isClone, resolvedRoutingType]);

  // Don't reset here - let useDropdownFormInitializer handle it after materials are loaded

  const onSubmit = async (data: RoutingFormData) => {
    try {
      // Use operations, operationRelationships, and resourceRequirements from state if they're not in form data
      // IMPORTANT: Always prefer currentValue.operations as it contains the most up-to-date data including primaryResourceTypeId
      
      let finalOperations: any[] = [];
      // Always prefer currentValue.operations as it contains the most up-to-date data (including primaryResourceTypeId)
      if (currentValue.operations && currentValue.operations.length > 0) {
        finalOperations = currentValue.operations;
      } else if (operations.length > 0) {
        finalOperations = operations;
      } else if (data.operations && data.operations.length > 0) {
        finalOperations = data.operations;
      } else {
        finalOperations = [];
      }
      
      
      // Always prefer currentValue as it contains the most up-to-date data. Whichever source
      // wins, one mapper builds the DTOs, so nulls from the form never reach the API through
      // a type cast (review finding on PR 112).
      const relationshipSource = (currentValue.operationRelationships && currentValue.operationRelationships.length > 0)
        ? currentValue.operationRelationships
        : (operationRelationships.length > 0
          ? operationRelationships
          : (data.operationRelationships || []));
      const finalOperationRelationships = toOperationRelationshipDtos(relationshipSource);

      const requirementSource = (currentValue.resourceRequirements && currentValue.resourceRequirements.length > 0)
        ? currentValue.resourceRequirements
        : (resourceRequirements.length > 0
          ? resourceRequirements
          : (data.resourceRequirements || []));
      const finalResourceRequirements = toResourceRequirementDtos(requirementSource);

      const routing: RoutingDto & { id: number } = {
        ...value,
        id: value?.id || 0,
        uuid: value?.uuid, // Will be undefined for new/cloned items
        name: data.name,
        description: data.description ?? undefined,
        tenantId: value?.tenantId,
        materialId: data.materialId,
        routingType: resolvedRoutingType,
        // Casting through unknown because the generated RoutingDto currently types weekdayMask
        // as string (OpenAPI codegen pre-regen). After the next `npm run generate-api` the
        // backend Integer surfaces as number on the wire and this cast can be removed.
        weekdayMask: (typeof data.weekdayMask === 'number' ? data.weekdayMask : 0) as unknown as RoutingDto['weekdayMask'],
        applicability: data.applicability ?? 'DEFAULT',
        operations: finalOperations.map(op => ({
          ...op,
          uuid: op.uuid || generateTempUUID(), // Ensure all operations have UUIDs
          routingId: value?.uuid || undefined, // Set routingId if routing has UUID
          durationSeconds: op.durationSeconds ?? undefined
        })) as OperationDto[],
        operationRelationships: finalOperationRelationships,
        resourceRequirements: finalResourceRequirements
      };
      
      await onSave(routing);
      // Only close dialog if save was successful
      setVisible(false);
    } catch (error) {
      console.error('❌ Error in onSubmit:', error);
      // Don't close dialog on error - let user fix the issue
      handleError(error, 'saving routing');
      // Re-throw error to ensure it's handled by parent (CrudPage)
      throw error;
    }
  };

  // react-hook-form calls onSubmit only when the resolver accepts the whole form. Without this
  // callback a rejected form did nothing at all: no request, no message, the dialog stayed open.
  // The dialog itself shows messages for name, material and description only, so errors inside
  // the nested operations, relationships and requirements are reported here.
  // The toast renders its content as HTML, so the lines are escaped and joined with <br/>,
  // and it stays long enough to read a multi-line summary.
  const onInvalid = (formErrors: typeof errors) => {
    const lines = summarizeRoutingFormErrors(formErrors, t);
    console.warn('RoutingFormDialog: validation rejected the routing', lines, formErrors);
    showToast(
      'warning',
      t('routing.form.validationFailed.title', 'Arbeitsplan kann nicht gespeichert werden'),
      lines.length > 0
        ? linesToHtml(lines)
        : t('routing.form.validationFailed.unknown', 'Bitte prüfen Sie die Eingaben.'),
      { timeOut: 10000 },
    );
  };

  const handleClose = () => {
    setVisible(false);
    onClose();
  };

  const handleMaterialChange = (args: any, setValue: any) => {
    setValue('materialId', args.value);
    const material = materials.find(m => m.uuid === args.value);
    setSelectedMaterial(material || null);
  };

  const handleInputQuantitiesSave = (inputQuantities: OperationInputQuantityDto[]) => {
    if (selectedOperation) {
      // Get the most up-to-date operations from the form state
      const currentOperations = watch('operations') || [];
      const updatedOperations = currentOperations.map(op => 
        op.uuid === selectedOperation.uuid 
          ? { ...op, inputQuantities }
          : op
      );
      setValue('operations', updatedOperations);
      trigger(); // Trigger validation
      // Close dialog immediately after save
      setInputQuantitiesDialogOpen(false);
      setSelectedOperation(null);
    }
  };


  const handleResourceSelectionSave = (resources: any[]) => {
    if (selectedResourceRequirement) {
      // Get the most up-to-date resource requirements from the form state
      const currentResourceRequirements = watch('resourceRequirements') || [];
      const updatedResourceRequirements = currentResourceRequirements.map(rr => 
        rr.uuid === selectedResourceRequirement.uuid 
          ? { ...rr, resources }
          : rr
      );
      setValue('resourceRequirements', updatedResourceRequirements);
      trigger(); // Trigger validation
      // Close dialog immediately after save
      setResourceSelectionDialogOpen(false);
      setSelectedResourceRequirement(null);
    }
  };

  // Create input quantities template
  const inputQuantitiesTemplate = (props: OperationDto) => {
    const count = props.inputQuantities?.length || 0;
    return <span>{count}</span>;
  };

  // Create execution modes template
  const executionModesTemplate = (props: OperationDto) => {
    const count = props.operationModes?.length || 0;
    return <span>{count}</span>;
  };

  return (
    <>
      <style>
        {`
          .routing-dialog .e-dlg-content {
            display: flex !important;
            flex-direction: column !important;
            min-height: 0 !important;
            height: 100% !important;
          }
          .routing-tabs-full-height .e-content {
            flex: 1 1 auto !important;
            display: flex !important;
            flex-direction: column !important;
            min-height: 0 !important;
          }
          .routing-tabs-full-height .e-content > div {
            flex: 1 1 auto !important;
            display: flex !important;
            flex-direction: column !important;
            min-height: 0 !important;
          }
          .routing-tabs-full-height .e-tab {
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
          }
          .routing-grid-container {
            flex: 1 1 auto !important;
            min-height: 0 !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .routing-grid-container .flex-1 {
            flex: 1 1 0% !important;
            min-height: 0 !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .routing-grid-container .e-grid {
            height: 100% !important;
            min-height: 0 !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .routing-grid-container .e-grid .e-gridheader,
          .routing-grid-container .e-grid .e-toolbar {
            flex-shrink: 0 !important;
          }
          .routing-grid-container .e-grid {
            height: 100% !important;
            max-height: 100% !important;
          }
          .routing-grid-container .e-grid .e-content table {
            display: table !important;
          }
          .routing-grid-container .e-grid .e-content .e-table {
            height: auto !important;
          }
        `}
      </style>
      <DialogComponent
        width="1400px"
        height="90vh"
        visible={visible}
        close={handleClose}
        showCloseIcon={true}
        isModal={true}
        cssClass="routing-dialog"
        header={isClone ? t('dialogs.routing.clone') : (value ? t('dialogs.routing.edit') : t('dialogs.routing.create'))}
        footerTemplate={() => (
          <div className="flex justify-end space-x-2">
            <ButtonComponent
              type="button"
              onClick={handleClose}
              cssClass="e-outline e-btn e-outline"
            >
              {t('actions.cancel')}
            </ButtonComponent>

            <ButtonComponent
              type="button"
              disabled={isSubmitting}
              cssClass="e-primary e-btn e-primary"
              onClick={() => {
                handleSubmit(onSubmit, onInvalid)();
              }}
            >
              {isSubmitting ? t('actions.saving') : t('actions.save')}
            </ButtonComponent>
          </div>
        )}
      >
        <div ref={dialogContentRef} className="p-4 flex flex-col gap-4 min-h-0 flex-1">
          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-4" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div ref={formTopRef} className="flex flex-col gap-4" style={{ flexShrink: 0 }}>
              {/* Basic Routing Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.name')} *
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextBoxComponent
                        value={field.value}
                        change={(e: any) => {
                          field.onChange(e.value || '');
                        }}
                        placeholder={t('form.name')}
                        cssClass={errors.name ? 'e-error' : ''}
                      />
                    )}
                  />
                  {errors.name && (
                    <div className="text-red-500 text-sm mt-1">{errors.name.message}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                      {materialLabel} *
                  </label>
                  <DropDownListComponent
                    dataSource={materials as any}
                    fields={{ text: 'name', value: 'uuid' }}
                    placeholder={materialLabel}
                    value={currentValue.materialId || ''}
                    change={(args) => {
                      handleMaterialChange(args, setValue);
                    }}
                    cssClass={errors.materialId ? 'e-error' : ''}
                    created={() => {
                    }}
                  />
                  {errors.materialId && (
                    <div className="text-red-500 text-sm mt-1">{errors.materialId.message}</div>
                  )}
                </div>
              </div>

              {/* Description Section */}
              <div>
                <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.description')}
                  </label>
                  <TextBoxComponent
                    {...register('description')}
                    placeholder={t('form.description')}
                    cssClass={errors.description ? 'e-error' : ''}
                  />
                  {errors.description && (
                    <div className="text-red-500 text-sm mt-1">{errors.description.message}</div>
                  )}
                  </div>

                </div>
              </div>

              {/* Geltungsbereich (applicability) — visible when the backend feature flag is on.
                  Dropdown picks one of DEFAULT / WEEKDAYS / HOLIDAY / INACTIVE. The weekday checkboxes
                  appear only for WEEKDAYS; the mask is preserved across applicability changes so an
                  INACTIVE routing keeps its day selection ready for reactivation. Backend enforces
                  uniqueness of DEFAULT/HOLIDAY per material+tenant and rejects overlapping WEEKDAYS
                  masks; the previous client-side "needsWeekdayPick" gate is gone. */}
              {variantsFeatureEnabled && (
                <Controller
                  name="applicability"
                  control={control}
                  render={({ field: { value: applicabilityValue, onChange: onApplicabilityChange } }) => {
                    const current = normalizeApplicability(applicabilityValue);
                    const options = APPLICABILITY_VALUES.map(v => ({
                      text: t(`routing.applicability.${v.toLowerCase()}`, v),
                      value: v,
                    }));
                    const helperKey = `routing.applicability.${current.toLowerCase()}Hint`;
                    const helperFallback: Record<RoutingApplicabilityValue, string> = {
                      DEFAULT: 'Fallback fuer alle Tage ohne explizite Variante. Maximal ein Standard pro Material.',
                      WEEKDAYS: 'Wochentag-Variante. Wähle die Tage; pro Material duerfen sich die Tag-Auswahlen nicht ueberlappen.',
                      HOLIDAY: 'Feiertag-Variante. Maximal ein Feiertags-Arbeitsplan pro Material.',
                      INACTIVE: 'Arbeitsplan ist geparkt und wird vom Resolver nicht ausgewaehlt. Tag-Auswahl bleibt fuer spaetere Reaktivierung erhalten.',
                    };
                    const helperText = t(helperKey, helperFallback[current]);

                    return (
                      <div className="p-2 bg-gray-50 border border-gray-200 rounded space-y-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                            {t('routing.applicability.title', 'Geltungsbereich')}:
                          </label>
                          <DropDownListComponent
                            dataSource={options}
                            fields={{ text: 'text', value: 'value' }}
                            value={current}
                            change={(args: any) => {
                              if (args?.isInteracted === false) return;
                              onApplicabilityChange(args.value as RoutingApplicabilityValue);
                            }}
                            width={220}
                          />
                          <span
                            className="text-gray-500 text-xs italic flex-1"
                            title={helperText}
                          >
                            {helperText}
                          </span>
                        </div>

                        {current === 'WEEKDAYS' && (
                          <Controller
                            name="weekdayMask"
                            control={control}
                            render={({ field: { value: maskValue, onChange: onMaskChange } }) => {
                              const currentMask = typeof maskValue === 'number'
                                ? maskValue
                                : Number(maskValue ?? 0);
                              const weekdayKeys = [
                                'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
                              ] as const;
                              const toggleBit = (bit: number) => {
                                // The mask stays 7 bits wide (Mon-Sun); bit 7 has been reserved/unused since V00138.
                                onMaskChange(((currentMask ^ (1 << bit)) & 0x7F));
                              };
                              return (
                                <div className="flex items-center gap-3 flex-wrap pl-1">
                                  {weekdayKeys.map((key, bit) => (
                                    <label key={key} className="flex items-center space-x-1 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={(currentMask & (1 << bit)) !== 0}
                                        onChange={() => toggleBit(bit)}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-sm">
                                        {t(`routing.variants.weekdayShort.${key}`, key)}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              );
                            }}
                          />
                        )}
                      </div>
                    );
                  }}
                />
              )}
            </div>

            {/* Tabbed Sections: Operations, Operation Relationships, Resource Requirements */}
            <div ref={tabsContainerRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <ReactTabs
                panelClassName="routing-tabs-full-height"
                style={{ flex: 1 }}
                items={[
                // Operations Tab
                {
                  header: t('form.operations'),
                  content: () => (
                    <div className="p-2 routing-grid-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {!nestedGridsReady ? (
                        <div
                          className="flex items-center justify-center text-gray-500 text-sm"
                          style={{ minHeight: gridHeightPx }}
                        >
                          {t('actions.loading')}
                        </div>
                      ) : (
                      <CrudPage
                  refreshKey={operationsRefreshKey}
                  titleKey="routing.form.operations"
                  showTitle={false}
                  noPadding={true}
                  gridHeight={`${gridHeightPx}px`}
                  localMode={true}
                  gridMeta={[
                    { field: 'name', headerKey: 'routing.form.operationName', width: 200 },
                    { field: 'description', headerKey: 'form.description', width: 200 },
                    { field: 'primaryResourceType', headerKey: 'routing.form.primaryResourceType', width: 150, render: (data: any) => {
                      // Read directly from OperationDto, not from operationModes
                      return data.primaryResourceTypeName || '-';
                    }},
                    { field: 'globalDuration', headerKey: 'routing.form.totalDuration', width: 120, render: (data: any) => {
                      // Read directly from OperationDto, not from operationModes
                      if (data.timeType === 'FIXED_DURATION' && typeof data.durationSeconds === 'number') {
                        const seconds = data.durationSeconds;
                        const hours = Math.floor(seconds / 3600);
                        const minutes = Math.floor((seconds % 3600) / 60);
                        const secs = seconds % 60;
                        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                      } else if (data.timeType === 'QUANTITY_DEPENDENT' && typeof data.durationPerPieceSeconds === 'number') {
                        const seconds = data.durationPerPieceSeconds;
                        const hours = Math.floor(seconds / 3600);
                        const minutes = Math.floor((seconds % 3600) / 60);
                        const secs = seconds % 60;
                        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')} / Stück`;
                      }
                      return '-';
                    }},
                    { field: 'minDistancePredecessor', headerKey: 'routing.form.minDistancePredecessor', width: 150, align: 'Center', render: (data: any) => {
                      const seconds = data.minDistancePredecessor;
                      if (seconds == null) return '-';
                      const hours = Math.floor(seconds / 3600);
                      const minutes = Math.floor((seconds % 3600) / 60);
                      const secs = seconds % 60;
                      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                    }},
                    { field: 'maxDistancePredecessor', headerKey: 'routing.form.maxDistancePredecessor', width: 150, align: 'Center', render: (data: any) => {
                      const seconds = data.maxDistancePredecessor;
                      if (seconds == null) return '-';
                      const hours = Math.floor(seconds / 3600);
                      const minutes = Math.floor((seconds % 3600) / 60);
                      const secs = seconds % 60;
                      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                    }},
                    { field: 'inputQuantities', align: "Center", headerKey: 'routing.form.inputQuantities', width: 120, render: inputQuantitiesTemplate },
                    { field: 'operationModes', align: "Center", headerKey: 'routing.form.executionModes', width: 120, render: executionModesTemplate }
                  ]}
                  fetchData={(): Promise<any[]> => {
                    
                    // Use operations state as primary source (updated immediately in handleOperationsPriorityChange)
                    // Fallback to currentValue.operations if operations state is empty
                    const sourceOperations = (operations && operations.length > 0) ? operations : (currentValue.operations || []);
                    
                    // Map uuid to id for CrudPage compatibility
                    // Sort by operationNumber when present, otherwise by index
                    const sortedOperations = [...sourceOperations].sort((a, b) => {
                      const aNum = (a as any).operationNumber ?? (sourceOperations.indexOf(a) + 1);
                      const bNum = (b as any).operationNumber ?? (sourceOperations.indexOf(b) + 1);
                      return aNum - bNum;
                    });
                    const operationsWithId = sortedOperations.map(op => ({
                          ...op,
                      id: op.uuid || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                      // Make sure operationNumber is set
                      operationNumber: (op as any).operationNumber ?? (sortedOperations.indexOf(op) + 1)
                    }));
                    
                    
                    return Promise.resolve(operationsWithId);
                  }}
                  saveData={async (operation: any) => {
                    
                    // Remove the id field and use uuid for the actual data
                    const { id, ...operationData } = operation;
                    
                    // Check if this is an update (operation with this uuid already exists) or a new operation
                    const existingOperation = (currentValue.operations || []).find(op => op.uuid === operationData.uuid);
                    
                    let updatedOperations: any[];
                    
                    if (existingOperation) {
                      // Update existing operation
                      // IMPORTANT: Always preserve operationModes if they're not in the new data
                      // This ensures we always send everything to the backend
                      const updatedOperation = {
                        ...operationData,
                        // Preserve operationModes from existing operation if not provided in new data
                        operationModes: (operationData as any).operationModes !== undefined && (operationData as any).operationModes !== null
                          ? (operationData as any).operationModes
                          : (existingOperation as any).operationModes
                      };
                      updatedOperations = (currentValue.operations || []).map(op => 
                        op.uuid === operationData.uuid ? updatedOperation : op
                      );
                    } else {
                      // New operation: Check for operationNumber collision and shift subsequent operations
                      const targetOperationNumber = (operationData as any).operationNumber;
                      const currentOps = currentValue.operations || [];
                      
                      // Find operations that need to be shifted (operationNumber >= targetOperationNumber)
                      const operationsToShift = currentOps.filter(op => {
                        const opNumber = (op as any).operationNumber || 0;
                        return opNumber >= targetOperationNumber;
                      });
                      
                      // Shift operationNumbers: increment by 1
                      const shiftedOperations = operationsToShift.map(op => ({
                        ...op,
                        operationNumber: ((op as any).operationNumber || 0) + 1
                      }));
                      
                      // Operations that don't need shifting
                      const unchangedOperations = currentOps.filter(op => {
                        const opNumber = (op as any).operationNumber || 0;
                        return opNumber < targetOperationNumber;
                      });
                      
                      // Combine: unchanged operations + new operation + shifted operations
                      updatedOperations = [
                        ...unchangedOperations,
                        { ...operationData, uuid: operationData.uuid || generateTempUUID() },
                        ...shiftedOperations
                      ];
                      
                      // Sort by operationNumber to ensure correct order
                      updatedOperations.sort((a, b) => {
                        const aNum = (a as any).operationNumber || 0;
                        const bNum = (b as any).operationNumber || 0;
                        return aNum - bNum;
                      });
                    }
                    
                    setValue('operations', updatedOperations);
                    setOperations(updatedOperations);
                    setOperationsRefreshKey(prev => prev + 1);
                  }}
                  deleteData={async (id: string) => {
                    // Find the operation by id (which maps to uuid) and remove it
                    const operationToDelete = (currentValue.operations || []).find(op => op.uuid === id);
                    if (!operationToDelete) return;
                    
                    // Base delete confirmation (phase 1: without dependency check)
                    const operationName = operationToDelete.name || 'Unbenannt';
                    if (window.confirm(t('messages.deleteOperationConfirm', { 
                      defaultValue: `Möchten Sie den Vorgang "${operationName}" wirklich löschen?`
                    }))) {
                      const sorted = [...(currentValue.operations || [])].sort((a, b) => {
                        const aNum = (a as any).operationNumber ?? 999;
                        const bNum = (b as any).operationNumber ?? 999;
                        return aNum - bNum;
                      });
                      const filteredOps = sorted.filter(op => op.uuid !== id);
                      const updatedOperations = filteredOps.map((op, index) => ({
                        ...op,
                        operationNumber: index + 1
                      }));
                      setValue('operations', updatedOperations);
                      setOperations(updatedOperations);
                      setOperationsRefreshKey(prev => prev + 1);
                    }
                  }}
                  FormComponent={OperationFormAdapter}
                  formProps={{ routingOutputMaterialId: currentValue.materialId || undefined }}
                  showDefaultToolbar={true}
                  showAddButton={true}
                  showEditButton={true}
                  showDeleteButton={true}
                  showSearchButton={false}
                  allowFiltering={false}
                  allowSorting={false}
                  showSummary={false}
                  hasPriority={false}
                  deletionMode="Single"
                  onAdd={handleOperationsAdd}
                  onDelete={handleOperationsDelete}
                  showContextMenu={true}
                  contextMenuItems={[
                    { id: 'addBefore', text: t('routing.form.contextMenu.addBefore', 'Hinzufügen davor'), iconCss: 'e-icons e-insert-above' },
                    { id: 'addAfter', text: t('routing.form.contextMenu.addAfter', 'Hinzufügen danach'), iconCss: 'e-icons e-insert-below' }
                  ]}
                  onContextMenuClick={handleOperationsContextMenuClick}
                      />
                      )}
                    </div>
                  ),
                },

                // Operation Relationships Tab
                {
                  header: t('routing.form.operationRelationships'),
                  content: () => (
                    <div className="p-2 routing-grid-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {!nestedGridsReady ? (
                        <div
                          className="flex items-center justify-center text-gray-500 text-sm"
                          style={{ minHeight: gridHeightPx }}
                        >
                          {t('actions.loading')}
                        </div>
                      ) : (
                      <CrudPage
                  refreshKey={operationRelationshipsRefreshKey}
                  titleKey="routing.form.operationRelationships"
                  showTitle={false}
                    noPadding={true}
                    gridHeight={`${gridHeightPx}px`}
                  localMode={true}
                  gridMeta={[
                    { field: 'predecessorName', headerKey: 'routing.form.predecessor', width: 150 },
                    { field: 'successorName', headerKey: 'routing.form.successor', width: 150 },
                    { field: 'constraintType', align: "Center", headerKey: 'routing.form.constraintType', width: 180, render: constraintTypeRenderer }
                  ]}
                  fetchData={(): Promise<any[]> => {
                    
                    // Use currentValue.operationRelationships as the source of truth since it's updated immediately
                    // Backend already filters to only return CONSTRAINT relationships
                    const sourceRelationships = currentValue.operationRelationships || [];
                    
                    // Use currentValue.operations as the source of truth for finding operation names
                    const sourceOperations = currentValue.operations || [];
                    
                    // Map operation relationships and add operation names for display
                    const relationshipsWithNames = sourceRelationships.map(rel => {
                      
                      // Try multiple ways to find the operations - including integer matching
                      const predecessor = sourceOperations.find(op => {
                        const matches = {
                          uuidMatch: op.uuid === rel.predecessorId,
                          idMatch: op.id === rel.predecessorId,
                          nameMatch: op.name === rel.predecessorId,
                          idStringMatch: String(op.id) === String(rel.predecessorId),
                          uuidStringMatch: String(op.uuid) === String(rel.predecessorId),
                          // Try matching by index if predecessorId is a number
                          indexMatch: typeof rel.predecessorId === 'string' && !isNaN(Number(rel.predecessorId)) && 
                                     sourceOperations.indexOf(op) === Number(rel.predecessorId)
                        };
                        return Object.values(matches).some(Boolean);
                      });
                      
                      const successor = sourceOperations.find(op => {
                        const matches = {
                          uuidMatch: op.uuid === rel.successorId,
                          idMatch: op.id === rel.successorId,
                          nameMatch: op.name === rel.successorId,
                          idStringMatch: String(op.id) === String(rel.successorId),
                          uuidStringMatch: String(op.uuid) === String(rel.successorId),
                          // Try matching by index if successorId is a number
                          indexMatch: typeof rel.successorId === 'string' && !isNaN(Number(rel.successorId)) && 
                                     sourceOperations.indexOf(op) === Number(rel.successorId)
                        };
                        return Object.values(matches).some(Boolean);
                      });
                      
                      
                      // Ensure all fields are preserved, especially minDistance, maxDistance, relationType, and resourceConstraints
                      // IMPORTANT: 0 is a valid value for maxDistance, so we need to check explicitly for undefined/null
                      const mappedRel = {
                        ...rel,
                        id: rel.uuid || generateTempUUID(),
                        predecessorName: predecessor?.name || `Operation ${rel.predecessorId}`,
                        successorName: successor?.name || `Operation ${rel.successorId}`,
                        relationshipCategory: (rel as any).relationshipCategory || 'TIME',
                        // Explicitly preserve these fields to ensure they're not lost
                        // minDistance: 0 is valid, so only check for undefined/null
                        minDistance: rel.minDistance !== undefined && rel.minDistance !== null 
                          ? (typeof rel.minDistance === 'string' ? Number(rel.minDistance) : rel.minDistance)
                          : 0,
                        // maxDistance: 0 is valid, so only check for undefined/null (not falsy)
                        maxDistance: rel.maxDistance !== undefined && rel.maxDistance !== null
                          ? (typeof rel.maxDistance === 'string' ? Number(rel.maxDistance) : rel.maxDistance)
                          : undefined,
                        relationType: rel.relationType || null,
                        resourceConstraints: rel.resourceConstraints ? {
                          ...rel.resourceConstraints,
                          constraintType: rel.resourceConstraints.constraintType || undefined,
                          predecessorResourceIds: rel.resourceConstraints.predecessorResourceIds || undefined,
                          successorResourceIds: rel.resourceConstraints.successorResourceIds || undefined
                        } : null
                      };
                      return mappedRel;
                    });
                    return Promise.resolve(relationshipsWithNames);
                  }}
                  saveData={async (relationship: any) => {
                    
                    // Remove the id field and use uuid for the actual data
                    const { id, predecessorName, successorName, ...relationshipData } = relationship;
                    
                    const updatedRelationships = relationshipData.uuid 
                      ? (currentValue.operationRelationships || []).map(rel => rel.uuid === relationshipData.uuid ? relationshipData : rel)
                      : [...(currentValue.operationRelationships || []), { ...relationshipData, uuid: relationshipData.uuid || generateTempUUID() }];
                    
                    setValue('operationRelationships', updatedRelationships);
                    setOperationRelationships(updatedRelationships);
                    setOperationRelationshipsRefreshKey(prev => prev + 1);
                  }}
                  deleteData={async (id: string) => {
                    
                    // Find the relationship by id (which maps to uuid) and remove it
                    const updatedRelationships = (currentValue.operationRelationships || []).filter(rel => rel.uuid !== id);
                    
                    setValue('operationRelationships', updatedRelationships);
                    setOperationRelationships(updatedRelationships);
                    setOperationRelationshipsRefreshKey(prev => prev + 1);
                  }}
                  FormComponent={OperationRelationshipFormAdapter}
                  formProps={{
                    availableOperations: (currentValue.operations || []).map(op => ({
                      ...op,
                      durationSeconds: op.durationSeconds ?? undefined
                    })) as OperationDto[],
                    allRelationships: (currentValue.operationRelationships || [])
                      .filter(rel => rel.predecessorId && rel.successorId && rel.relationshipCategory)
                      .map(rel => ({
                        ...rel,
                        predecessorId: rel.predecessorId!,
                        successorId: rel.successorId!,
                        relationshipCategory: rel.relationshipCategory!,
                        resourceConstraints: rel.resourceConstraints ? {
                          ...rel.resourceConstraints,
                          constraintType: rel.resourceConstraints.constraintType || undefined
                        } : undefined
                      })) as OperationRelationshipDto[],
                    routingMaterialId: currentValue.materialId,
                  }}
                  showDefaultToolbar={true}
                  showAddButton={true}
                  showEditButton={true}
                  showDeleteButton={true}
                  showSearchButton={false}
                  allowFiltering={false}
                  allowSorting={false}
                  showSummary={false}
                      />
                      )}
                    </div>
                  ),
                },

                // Resource Requirements Tab
                {
                  header: t('form.resourceRequirements'),
                  content: () => (
                    <div className="p-2 routing-grid-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {!nestedGridsReady ? (
                        <div
                          className="flex items-center justify-center text-gray-500 text-sm"
                          style={{ minHeight: gridHeightPx }}
                        >
                          {t('actions.loading')}
                        </div>
                      ) : (
                      <CrudPage
                  refreshKey={resourceRequirementsRefreshKey}
                  titleKey="routing.form.resourceRequirements"
                  showTitle={false}
                    noPadding={true}
                    gridHeight={`${gridHeightPx}px`}
                  localMode={true}
                  gridMeta={[
                    { 
                      field: 'fromRelation', 
                      headerKey: 'routing.form.fromRelation', 
                      width: 100,
                      render: (data: any) => <span>{String(t(`form.relationType.${data.fromRelation}`, data.fromRelation))}</span>
                    },
                    { 
                      field: 'fromOperationId', 
                      headerKey: 'routing.form.fromOperation', 
                      width: 180,
                      render: (data: any) => {
                        const operation = (currentValue.operations || []).find(op => op.uuid === data.fromOperationId);
                        return operation?.name || `ID: ${data.fromOperationId}`;
                      }
                    },
                    { 
                      field: 'toRelation', 
                      headerKey: 'routing.form.toRelation', 
                      width: 100,
                      render: (data: any) => <span>{String(t(`form.relationType.${data.toRelation}`, data.toRelation))}</span>
                    },
                    { 
                      field: 'toOperationId', 
                      headerKey: 'routing.form.toOperation', 
                      width: 180,
                      render: (data: any) => {
                        const operation = (currentValue.operations || []).find(op => op.uuid === data.toOperationId);
                        return operation?.name || `ID: ${data.toOperationId}`;
                      }
                    },
                    { 
                      field: 'resources',
                      align: "Center",
                      headerKey: 'routing.form.numResources', 
                      width: 100,
                      render: (data: any) => data.resources?.length || 0
                    },
                    { 
                      field: 'quantity', 
                      align: "Center",
                      headerKey: 'routing.form.quantity', 
                      width: 100,
                      render: (data: any) => {
                        // Calculate total quantity from all resources
                        const totalQuantity = data.resources?.reduce((sum: number, r: any) => sum + (r.quantity || 0), 0) || 0;
                        return totalQuantity;
                      }
                    },
                    { 
                      field: 'conditional', 
                      headerKey: 'routing.form.conditional', 
                      width: 120,
                      align: 'Center',
                      render: (data: any) => {
                        const isConditional = data.conditional === true || data.conditional === 'true';
                        return (
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={isConditional}
                              disabled
                              readOnly
                              className="cursor-not-allowed"
                            />
                          </div>
                        );
                      }
                    }
                  ]}
                  fetchData={(): Promise<any[]> => {
                    // Always use resourceRequirements state as the source of truth
                    // This state is updated synchronously in deleteData/saveData, ensuring we get the latest data immediately
                    // The useEffect at line 801-806 keeps this state in sync with currentValue.resourceRequirements
                    const sourceRequirements = resourceRequirements;
                    return Promise.resolve(sourceRequirements.map(req => ({ ...req, id: req.uuid })));
                  }}
                  saveData={async (requirement: any) => {
                    
                    // Remove the id field and use uuid for the actual data
                    const { id, ...requirementData } = requirement;
                    // Ensure uuid is set - use uuid if available, otherwise use id (which should be the uuid)
                    const requirementUuid = requirementData.uuid || id;
                    
                    // Set uuid if not already set
                    if (!requirementData.uuid && requirementUuid) {
                      requirementData.uuid = requirementUuid;
                    }
                    
                    const updatedRequirements = requirementUuid 
                      ? (currentValue.resourceRequirements || []).map(req => req.uuid === requirementUuid ? requirementData : req)
                      : [...(currentValue.resourceRequirements || []), { ...requirementData, uuid: generateTempUUID() }];
                    
                    setValue('resourceRequirements', updatedRequirements);
                    setResourceRequirements(updatedRequirements);
                    setResourceRequirementsRefreshKey(prev => prev + 1);
                  }}
                  deleteData={async (id: string) => {
                    
                    // Use resourceRequirements state as source of truth since it's updated synchronously
                    // Fallback to currentValue if state is empty (shouldn't happen, but safety check)
                    const sourceRequirements = resourceRequirements.length > 0 ? resourceRequirements : (currentValue.resourceRequirements || []);
                    
                    // Find the requirement by id (which maps to uuid) and remove it
                    const updatedRequirements = sourceRequirements.filter(req => req.uuid !== id);
                    
                    setValue('resourceRequirements', updatedRequirements);
                    setResourceRequirements(updatedRequirements);
                    setResourceRequirementsRefreshKey(prev => prev + 1);
                  }}
                  FormComponent={ResourceRequirementFormAdapter}
                  formProps={{
                    availableOperations: (currentValue.operations || []).map(op => ({
                      ...op,
                      durationSeconds: op.durationSeconds ?? undefined
                    })) as OperationDto[],
                  }}
                  showDefaultToolbar={true}
                  showAddButton={true}
                  showEditButton={true}
                  showDeleteButton={true}
                  showSearchButton={false}
                  allowFiltering={false}
                  allowSorting={false}
                  showSummary={false}
                      />
                      )}
                    </div>
                  ),
                },
                ]}
              />
            </div>
          </form>
        </div>
      </DialogComponent>

      {/* Input Quantities Dialog */}
      <InputQuantitiesDialog
        isOpen={inputQuantitiesDialogOpen && !!selectedOperation}
                  onClose={() => {
            setInputQuantitiesDialogOpen(false);
            setSelectedOperation(null);
          }}
        onSave={handleInputQuantitiesSave}
        initialQuantities={selectedOperation?.inputQuantities || []}
        operationName={selectedOperation?.name || ''}
      />


      {/* Resource Selection Dialog */}
      <ResourceSelectionDialog
        isOpen={resourceSelectionDialogOpen && !!selectedResourceRequirement}
                  onClose={() => {
            setResourceSelectionDialogOpen(false);
            setSelectedResourceRequirement(null);
          }}
        onSave={handleResourceSelectionSave}
        initialResources={selectedResourceRequirement?.resources || []}
        resourceRequirementName={selectedResourceRequirement ? `${selectedResourceRequirement.fromOperationId} → ${selectedResourceRequirement.toOperationId}` : ''}
      />

      {/* Simple Delete Operations Confirmation Dialog (no dependencies) */}
      {deleteOperationsSimpleDialogOpen && (
        <DialogComponent
          visible={deleteOperationsSimpleDialogOpen}
          isModal={true}
          header={t('actions.delete')}
          showCloseIcon={true}
          closeOnEscape={true}
          width="400px"
          close={() => {
            setDeleteOperationsSimpleDialogOpen(false);
            setPendingDeleteOperationsSimple([]);
          }}
          animationSettings={{ effect: 'None' }}
          footerTemplate={() => (
            <div className="flex justify-end gap-2">
              <ButtonComponent 
                onClick={() => {
                  setDeleteOperationsSimpleDialogOpen(false);
                  setPendingDeleteOperationsSimple([]);
                }} 
                cssClass="e-outline e-btn e-outline"
                type="button"
              >
                {t('actions.cancel')}
              </ButtonComponent>
              <ButtonComponent 
                onClick={confirmDeleteOperationsSimple} 
                cssClass="e-primary e-btn e-primary"
                type="button"
              >
                {t('actions.delete')}
              </ButtonComponent>
            </div>
          )}
        >
          <div className="p-4">
            <p>
              {t('messages.deleteOperationConfirm', {
                operationNames: pendingDeleteOperationsSimple.map(op => op.name || 'Unbenannt').join(', '),
                defaultValue: `Möchten Sie die Vorgänge "${pendingDeleteOperationsSimple.map(op => op.name || 'Unbenannt').join(', ')}" wirklich löschen?`
              })}
            </p>
          </div>
        </DialogComponent>
      )}

      {/* Delete Operations Confirmation Dialog */}
      {deleteOperationsDialogOpen && (
        <DialogComponent
          visible={deleteOperationsDialogOpen}
          isModal={true}
          header={t('actions.delete')}
          showCloseIcon={true}
          closeOnEscape={true}
          width="500px"
          close={() => {
            setDeleteOperationsDialogOpen(false);
            setPendingDeleteOperations([]);
            setDeleteOperationsMessage('');
          }}
          animationSettings={{ effect: 'None' }}
          footerTemplate={() => (
            <div className="flex justify-end gap-2">
              <ButtonComponent 
                onClick={() => {
                  setDeleteOperationsDialogOpen(false);
                  setPendingDeleteOperations([]);
                  setDeleteOperationsMessage('');
                }} 
                cssClass="e-outline e-btn e-outline"
                type="button"
              >
                {t('actions.cancel')}
              </ButtonComponent>
              <ButtonComponent 
                onClick={confirmDeleteOperations} 
                cssClass="e-primary e-btn e-primary"
                type="button"
              >
                {t('actions.delete')}
              </ButtonComponent>
            </div>
          )}
        >
          <div className="p-4">
            <p dangerouslySetInnerHTML={{ __html: deleteOperationsMessage }} />
          </div>
        </DialogComponent>
      )}

      {/* Operation Move Info Dialog */}
      {moveInfoDialogOpen && movedOperation && movedOperationRelationships && (
        <DialogComponent
          visible={moveInfoDialogOpen}
          isModal={true}
          header={t('messages.operationMoved.title', { defaultValue: 'Vorgang verschoben' })}
          showCloseIcon={true}
          closeOnEscape={true}
          width="500px"
          close={() => {
            setMoveInfoDialogOpen(false);
            setMovedOperation(null);
            setMovedOperationRelationships(null);
          }}
          animationSettings={{ effect: 'None' }}
          footerTemplate={() => (
            <div className="flex justify-end gap-2">
              <ButtonComponent 
                onClick={() => {
                  setMoveInfoDialogOpen(false);
                  setMovedOperation(null);
                  setMovedOperationRelationships(null);
                }} 
                cssClass="e-primary e-btn e-primary"
                type="button"
              >
                {t('actions.ok', 'OK')}
              </ButtonComponent>
            </div>
          )}
        >
          <div className="p-4">
            <p className="mb-3">
              {t('messages.operationMoved.message', { 
                operationName: movedOperation.name || 'Unbenannt',
                defaultValue: `Die Operation "${movedOperation.name || 'Unbenannt'}" wurde verschoben.`
              })}
            </p>
            
            {(movedOperationRelationships.asPredecessor.length > 0 || movedOperationRelationships.asSuccessor.length > 0) && (
              <>
                <p className="mb-2 font-semibold">
                  {t('messages.operationMoved.hasRelationships', { defaultValue: 'Diese Operation hat folgende Beziehungen:' })}
                </p>
                
                {movedOperationRelationships.asPredecessor.length > 0 && (
                  <div className="mb-3">
                    <p className="mb-1 font-medium">
                      {t('messages.operationMoved.asPredecessor', { defaultValue: 'Als Vorgänger (Predecessor):' })}
                    </p>
                    <ul className="list-disc list-inside ml-2">
                      {movedOperationRelationships.asPredecessor.map((rel, idx) => {
                        const succOp = (currentValue.operations || []).find(op => op.uuid === rel.successorId);
                        const succName = succOp?.name || 'Unbekannt';
                        const relationType = rel.relationType || 'UNKNOWN';
                        return (
                          <li key={idx}>
                            {t('messages.operationMoved.relationshipItem', {
                              predName: movedOperation.name || 'Unbenannt',
                              succName,
                              relationType,
                              defaultValue: `${movedOperation.name || 'Unbenannt'} ──(${relationType})──> ${succName}`
                            })}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                
                {movedOperationRelationships.asSuccessor.length > 0 && (
                  <div className="mb-3">
                    <p className="mb-1 font-medium">
                      {t('messages.operationMoved.asSuccessor', { defaultValue: 'Als Nachfolger (Successor):' })}
                    </p>
                    <ul className="list-disc list-inside ml-2">
                      {movedOperationRelationships.asSuccessor.map((rel, idx) => {
                        const predOp = (currentValue.operations || []).find(op => op.uuid === rel.predecessorId);
                        const predName = predOp?.name || 'Unbekannt';
                        const relationType = rel.relationType || 'UNKNOWN';
                        return (
                          <li key={idx}>
                            {t('messages.operationMoved.relationshipItem', {
                              predName,
                              succName: movedOperation.name || 'Unbenannt',
                              relationType,
                              defaultValue: `${predName} ──(${relationType})──> ${movedOperation.name || 'Unbenannt'}`
                            })}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                
                <p className="mt-3 text-sm text-gray-600">
                  {t('messages.operationMoved.note', { 
                    defaultValue: 'Hinweis: Die Beziehungen bleiben erhalten. Die Reihenfolge der Operations hat sich geändert.'
                  })}
                </p>
              </>
            )}
          </div>
        </DialogComponent>
      )}

      {/* Relationship Suggestion Dialog */}
      {relationshipSuggestionDialogOpen && relationshipSuggestionData && (
        <DialogComponent
          visible={relationshipSuggestionDialogOpen}
          isModal={true}
          header={t('messages.relationshipSuggestion.title', { defaultValue: 'Vorgangsbeziehung anpassen?' })}
          showCloseIcon={true}
          closeOnEscape={true}
          width="600px"
          close={() => {
            setRelationshipSuggestionDialogOpen(false);
            setRelationshipSuggestionData(null);
            setRelationshipSuggestionOption('resolve');
          }}
          animationSettings={{ effect: 'None' }}
          footerTemplate={() => (
            <div className="flex justify-end gap-2">
              <ButtonComponent 
                onClick={() => {
                  setRelationshipSuggestionDialogOpen(false);
                  setRelationshipSuggestionData(null);
                  setRelationshipSuggestionOption('resolve');
                }} 
                cssClass="e-outline e-btn e-outline"
                type="button"
              >
                {t('actions.cancel')}
              </ButtonComponent>
              <ButtonComponent 
                onClick={() => {
                  if (!relationshipSuggestionData) return;
                  
                  const { predecessor, successor, newOperation, existingRelationship } = relationshipSuggestionData;
                  const currentRels = currentValue.operationRelationships || [];
                  
                  if (relationshipSuggestionOption === 'resolve') {
                    // Option 1: dissolve and recreate
                    const updatedRels = currentRels.filter(rel => rel.uuid !== existingRelationship.uuid);
                    
                    // Create A -> X (same parameters as A -> B)
                    if (!predecessor.uuid || !newOperation.uuid || !existingRelationship.relationshipCategory) {
                      console.error('Cannot create relationship: missing required fields');
                      return;
                    }
                    const relAX: OperationRelationshipDto = {
                      ...existingRelationship,
                      uuid: generateTempUUID(),
                      predecessorId: predecessor.uuid,
                      successorId: newOperation.uuid,
                      relationshipCategory: existingRelationship.relationshipCategory,
                      minDistance: existingRelationship.minDistance ?? undefined,
                      maxDistance: existingRelationship.maxDistance ?? undefined,
                      resourceConstraints: existingRelationship.resourceConstraints ? {
                        ...existingRelationship.resourceConstraints,
                        constraintType: existingRelationship.resourceConstraints.constraintType || undefined
                      } : undefined
                    };
                    
                    // Create X -> B (same parameters as A -> B)
                    if (!newOperation.uuid || !successor.uuid) {
                      console.error('Cannot create relationship: missing required fields');
                      return;
                    }
                    const relXB: OperationRelationshipDto = {
                      ...existingRelationship,
                      uuid: generateTempUUID(),
                      predecessorId: newOperation.uuid,
                      successorId: successor.uuid,
                      relationshipCategory: existingRelationship.relationshipCategory,
                      minDistance: existingRelationship.minDistance ?? undefined,
                      maxDistance: existingRelationship.maxDistance ?? undefined,
                      resourceConstraints: existingRelationship.resourceConstraints ? {
                        ...existingRelationship.resourceConstraints,
                        constraintType: existingRelationship.resourceConstraints.constraintType || undefined
                      } : undefined
                    };
                    
                    updatedRels.push(relAX, relXB);
                    setValue('operationRelationships', updatedRels);
                  } else if (relationshipSuggestionOption === 'delete') {
                    // Option 3: delete
                    const updatedRels = currentRels.filter(rel => rel.uuid !== existingRelationship.uuid);
                    setValue('operationRelationships', updatedRels);
                  }
                  // Option 2: keep - no change
                  
                  setRelationshipSuggestionDialogOpen(false);
                  setRelationshipSuggestionData(null);
                  setRelationshipSuggestionOption('resolve');
                }} 
                cssClass="e-primary e-btn e-primary"
                type="button"
              >
                {t('actions.ok', 'Übernehmen')}
              </ButtonComponent>
            </div>
          )}
        >
          <div className="p-4">
            <p className="mb-3">
              {t('messages.relationshipSuggestion.message', { 
                defaultValue: 'Sie fügen einen neuen Vorgang zwischen zwei Vorgängen ein, die bereits eine zeitliche Abhängigkeit haben:'
              })}
            </p>
            
            <p className="mb-4 font-mono text-center">
              {t('messages.relationshipSuggestion.existingRelationship', {
                predName: relationshipSuggestionData.predecessor.name || 'Unbekannt',
                succName: relationshipSuggestionData.successor.name || 'Unbekannt',
                defaultValue: `${relationshipSuggestionData.predecessor.name || 'Unbekannt'} ──(zeitlich)──> ${relationshipSuggestionData.successor.name || 'Unbekannt'}`
              })}
            </p>
            
            <p className="mb-4 font-semibold">
              {t('messages.relationshipSuggestion.question', { 
                defaultValue: 'Möchten Sie die Beziehung automatisch anpassen?'
              })}
            </p>
            
            <div className="space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="relationshipOption"
                  value="resolve"
                  checked={relationshipSuggestionOption === 'resolve'}
                  onChange={() => setRelationshipSuggestionOption('resolve')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">
                    {t('messages.relationshipSuggestion.optionResolve', { defaultValue: 'Beziehung auflösen und neu erstellen:' })}
                  </div>
                  <div className="text-sm text-gray-600 ml-6 font-mono">
                    {t('messages.relationshipSuggestion.optionResolveResult', {
                      predName: relationshipSuggestionData.predecessor.name || 'Unbekannt',
                      newOpName: relationshipSuggestionData.newOperation.name || 'Unbenannt',
                      succName: relationshipSuggestionData.successor.name || 'Unbekannt',
                      defaultValue: `${relationshipSuggestionData.predecessor.name || 'Unbekannt'} ──(zeitlich)──> ${relationshipSuggestionData.newOperation.name || 'Unbenannt'} ──(zeitlich)──> ${relationshipSuggestionData.successor.name || 'Unbekannt'}`
                    })}
                  </div>
                </div>
              </label>
              
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="relationshipOption"
                  value="keep"
                  checked={relationshipSuggestionOption === 'keep'}
                  onChange={() => setRelationshipSuggestionOption('keep')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">
                    {t('messages.relationshipSuggestion.optionKeep', {
                      predName: relationshipSuggestionData.predecessor.name || 'Unbekannt',
                      succName: relationshipSuggestionData.successor.name || 'Unbekannt',
                      defaultValue: `Beziehung beibehalten (${relationshipSuggestionData.predecessor.name || 'Unbekannt'} → ${relationshipSuggestionData.successor.name || 'Unbekannt'} bleibt bestehen)`
                    })}
                  </div>
                </div>
              </label>
              
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="relationshipOption"
                  value="delete"
                  checked={relationshipSuggestionOption === 'delete'}
                  onChange={() => setRelationshipSuggestionOption('delete')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">
                    {t('messages.relationshipSuggestion.optionDelete', { defaultValue: 'Beziehung löschen' })}
                  </div>
                </div>
              </label>
            </div>
          </div>
        </DialogComponent>
      )}

      {/* Dialog for the context menu insert (before/after) */}
      {operationDetailDialogOpen && pendingInsertOperation && (
        <OperationDetailDialog
          value={{
            ...pendingInsertOperation,
            uuid: pendingInsertOperation.id || pendingInsertOperation.uuid
          }}
          onClose={() => {
            setOperationDetailDialogOpen(false);
            setPendingInsertOperation(null);
          }}
          onSave={(operationData: any) => {
            // Map uuid back to id for consistency
            const operationWithId = { ...operationData, id: operationData.uuid };
            handleInsertOperationSave(operationWithId);
          }}
          routingOutputMaterialId={currentValue.materialId || undefined}
        />
      )}

    </>
  );
};

export default RoutingFormDialog;
